import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { updateTextField, updateCheckedField, translateFieldShiftFlag } from '../helpers';
import toast from 'react-hot-toast';

const TextField = ({ field, minLength = -1, inputFilter = null, onBackendSync = null }) => {
    if (!field) {
        return (
            <>
                <textarea
                    className={`input`}
                    value={`Phantom box`}
                    rows="1"
                    disabled={true}
                />
                <span
                    className='input-updated-at-label'
                >
                    Updated: Never
                </span>
            </>
        );
    }

    const [value, setValue] = useState(field.value);
    const [id, setID] = useState(field.parent);
    const textareaRef = useRef(null);
    const [fieldShift, setFieldShift] = useState(translateFieldShiftFlag(field.shift_flag));
    const userShift = useSelector((state) => state.shift);
    const [maxLength, setMaxLength] = useState(field.max_length);
    const briefing = useSelector((state) => state.briefing.line);

    let disabled = true
    if (briefing === "Briefings") {
        disabled = false
    }

    useEffect(() => {
        const checkAndResize = () => {
            const textarea = textareaRef.current;
            if (textarea && textarea.offsetParent !== null) { // visible
                textarea.style.height = 'auto';
                textarea.style.height = `${textarea.scrollHeight}px`;
            } else {
                setTimeout(checkAndResize, 100); // try again later
            }
        };

        checkAndResize();
    }, []);

    useEffect(() => {
        if (textareaRef.current) {
            // Adjust height
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [value]);

    const handleInputChange = (event) => {
        setValue(event.target.value);
    };

    const handleBlur = () => {
        if (value !== field.value) {
            const tempMaxLength = maxLength + 1
            if (value.length < tempMaxLength && value.length > minLength) {
                if (!inputFilter || inputFilter.filter(value)) {
                    updateTextField(id, value, userShift);
                    setFieldShift(userShift)
                    if (onBackendSync) {
                        onBackendSync()
                    }
                }
                else {
                    setFieldShift('error-shift')
                    toast.error(inputFilter.message)
                }
            }
            else {
                setFieldShift('error-shift')
                if (value.length > tempMaxLength) {
                    toast.error('Text too long')
                }
                if (value.length < minLength) {
                    toast.error('Text too short')
                }
            }
        }
    };

    return (
        <>
            <textarea
                maxLength={maxLength}
                minLength={minLength}
                ref={textareaRef}
                className={`input autoExpand ${fieldShift}`}
                value={value}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="Hungry box"
                rows="1" // Start with one row
                disabled={disabled}
            />
            <span
                className='input-updated-at-label'
            >
                Updated: {field.updated_at}
            </span>
        </>
    );
};

const SelectField = ({ field, children, placeholder = 'Empty select', onBackendSync = null }) => {
    if (!field) {
        return (
            <>
                <textarea
                    className={`input`}
                    value={`Phantom box`}
                    rows="1"
                    disabled={true}
                />
                <span
                    className='input-updated-at-label'
                >
                    Updated: Never
                </span>
            </>
        );
    }

    const [value, setValue] = useState(field.value);
    const [id, setID] = useState(field.parent);
    const [fieldShift, setFieldShift] = useState(translateFieldShiftFlag(field.shift_flag));
    const userShift = useSelector((state) => state.shift);
    const briefing = useSelector((state) => state.briefing.line);

    let disabled = true
    if (briefing === "Briefings") {
        disabled = false
    }

    const handleSelectChange = (event) => {
        setValue(event.target.value);
    };

    useEffect(() => {
        if (value !== field.value) {
            updateTextField(id, value, userShift);
            setFieldShift(userShift)
            if (onBackendSync) {
                onBackendSync()
            }
        }
    }, [value]);

    return (
        <>
            <div className='briefing-table-select-wrapper'>
                <select
                    className={`input ${fieldShift}`}
                    value={value}
                    onChange={handleSelectChange}
                    disabled={disabled}
                >
                    <option default value={null} hidden>
                        <div style={{ fontWeight: "lighter" }}>{placeholder}</div>
                    </option>
                    {children.length > 0 ? (
                        React.Children.map(children, (child, index) => (
                            React.cloneElement(child, { key: index })
                        ))
                    ) : (
                        null
                    )}
                    {value && !React.Children.toArray(children).some(child => child.props.value === value) && (
                        <option key="custom" value={value}>
                            {value}
                        </option>
                    )}
                </select>
            </div>
            <span
                className='input-updated-at-label'
            >
                Updated: {field.updated_at}
            </span>
        </>
    );
};

const Checkbox = ({ field }) => {
    const [checked, setChecked] = useState(field.value);
    const [id, setID] = useState(field.parent);
    const [fieldShift, setFieldShift] = useState(translateFieldShiftFlag(field.shift_flag));
    const userShift = useSelector((state) => state.shift);
    const briefing = useSelector((state) => state.briefing.line);

    let disabled = true
    if (briefing === "Briefings") {
        disabled = false
    }

    const handleChange = (event) => {
        if (!disabled) {
            setChecked(event.target.checked);
        }
    };

    useEffect(() => {
        if (checked !== field.value) {
            updateTextField(id, checked, userShift);
            setFieldShift(userShift)
            field.value = checked
        }
    }, [checked]);

    return (
        <div className="checkbox-input">
            <input
                type="checkbox"
                className="checkbox__input"
                id={`checkbox-${id}`}
                checked={checked}
                onChange={handleChange}
            />
            <label className="checkbox__label" htmlFor={`checkbox-${id}`}>
                <div className="checkbox__custom">
                    <span
                        className={`checkbox__custom-fill ${fieldShift}-checkbox`}
                    >
                    </span>
                </div>
                <span className="checkbox__text">{checked ? "Yes" : "No"}</span>
            </label>
        </div>
    );
};

const ExpandableTextField = ({ defaultValue, maxLength = 10, inputFilter = null, onBackendSync = null }) => {
    if (!field) {
        return (
            <>
                <textarea
                    className={`input`}
                    value={`Phantom box`}
                    rows="1"
                    disabled={true}
                />
            </>
        );
    }

    const [value, setValue] = useState(defaultValue);
    const textareaRef = useRef(null);

    useEffect(() => {
        if (textareaRef.current) {
            // Adjust height
            textareaRef.current.style.height = "auto";
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [value]);

    const handleInputChange = (event) => {
        setValue(event.target.value);
    };

    const handleBlur = () => {
        if (value !== defaultValue) {
            const tempMaxLength = maxLength + 1
            if (value.length < tempMaxLength) {
                if (!inputFilter || inputFilter.filter(value)) {
                    // Call the function to update the field in the backend
                    if (onBackendSync) {
                        onBackendSync()
                    }
                }
                else {
                    toast.error(inputFilter.message)
                }
            }
            else {
                toast.error('Text too long')
            }
        }
    }

    return (
        <>
            <textarea
                maxLength={maxLength}
                ref={textareaRef}
                className={`input autoExpand`}
                value={value}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder="Hungry box"
                rows="1" // Start with one row
            />
        </>
    );
};

export { TextField, SelectField, Checkbox, ExpandableTextField };

